import { Button, Dropdown, Form, Input, Menu, Spin, Tabs, Tooltip, Tag } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import IconGlyph from '../../assets/images/iconGlyph.svg'; // Import the icon
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api'; // Import axios for API calls
import { logUserAction } from '../../utils/logger';
import CommodityDaily from './CommodityDaily';
import CommodityMonthly from './CommodityMonthly';
import CommodityTable from './CommodityTable';
import CommodityVerification from './CommodityVerification';
import CommodityComparisonTable from './CommodityComparisonTable';
import ReportSubmitted from './ReportSubmitted'; // Import the new component

const { TabPane } = Tabs;

const Commodity = () => {
  const [activeKey, setActiveKey] = useState('tradeDaily');
  const [showForm, setShowForm] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const [filterVisible, setFilterVisible] = useState(false); // State for dropdown visibility
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyReportSubmitted, setDailyReportSubmitted] = useState(false);
  const [monthlyReportSubmitted, setMonthlyReportSubmitted] = useState(false);
  const [dailySubmissionDate, setDailySubmissionDate] = useState(null);
  const [monthlySubmissionDate, setMonthlySubmissionDate] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const { currentContactStore } = useContactStore();

  const isAfter4th = moment().date() > 4;

  const fetchData = async () => {
    setLoading(true); // Start loading
    try {
      console.log('Fetching daily and monthly data...');
      const [arrayDailyResponse, arrayMonthlyResponse] = await Promise.all([
        apiInstance(`${process.env.REACT_APP_API_URL}/api/crm/get-data?type=commodity-daily`),
        apiInstance(`${process.env.REACT_APP_API_URL}/api/crm/get-data?type=commodity-mounthly`),
      ]);

      // Process daily data
      console.log('Raw daily response:', arrayDailyResponse);
      if (arrayDailyResponse) {
        const mappedDailyData = arrayDailyResponse.map((item) => ({
          id: item.Id,
          name: item.SuName,
          amountWithoutVAT: item.SuAmountWithoutVAT,
          cash: item.RespCash,
          vat: item.RespVAT,
          amountWithVAT: item.SuAmountWithVAT,
          exciseTax: item.RespExciseTax,
          status: item['RespStatusTurnover.Name'],
          date: item.SuDate,
          isOnlineSales: item['RespTurnoverType.Id'] === 'eda64442-0816-44c0-ac04-41d49196188a',
          dataSource: item['RespDataSourceTO.Id'],
          submitDate: item.RespTurnoverSubmitDate || null,
          confirmDate: item.RespTurnoverConfirmDate || null,
        }));
        console.log('Mapped daily data:', mappedDailyData);
        setDailyData(mappedDailyData);

        // Check if a daily report was submitted for today
        const yesterday = moment().subtract(1, 'day').startOf('day');
        const dailyReport = mappedDailyData.find((item) => {
          const itemDate = moment(item.date).startOf('day');
          return itemDate.isSame(yesterday, 'day') && item.status !== 'Rejected';
        });
        setDailyReportSubmitted(!!dailyReport);
        setDailySubmissionDate(dailyReport ? dailyReport.date : null);
      } else {
        console.error('Daily data is undefined or empty.');
      }

      // Process monthly data
      console.log('Raw monthly response:', arrayMonthlyResponse);
      if (arrayMonthlyResponse) {
        const mappedMonthlyData = arrayMonthlyResponse.map((item) => ({
          id: item.Id,
          name: item.SuName,
          amountWithoutVAT: item.SuAmountWithoutVAT,
          cash: item.RespCash,
          vat: item.RespVAT,
          amountWithVAT: item.SuAmountWithVAT,
          exciseTax: item.RespExciseTax,
          status: item['RespStatusTurnover.Name'],
          date: item.SuDate,
          createdOn: item.CreatedOn,
          modifiedOn: item.ModifiedOn,
          isOnlineSales: item['RespTurnoverType.Id'] === 'eda64442-0816-44c0-ac04-41d49196188a',
          dataSource: item['RespDataSourceTO.Id'],
          turnoverApproving: item.RespTurnoverApproving,
          submitDate: item.RespTurnoverSubmitDate || null,
          confirmDate: item.RespTurnoverConfirmDate || null,
        }));
        console.log('Mapped monthly data:', mappedMonthlyData);
        setMonthlyData(mappedMonthlyData);
        logUserAction({
          action: 'FETCH_MONTHLY_TURNOVER_SUCCESS',
          level: 'info',
          message: 'Успішно завантажено дані місячного товарообігу',
          storeCrmId: currentContactStore?.crmId,
          details: { count: mappedMonthlyData.length },
        });

        // Check if a monthly report was submitted for the previous month
        const lastMonth = moment().subtract(1, 'month').startOf('month');
        const monthlyReport = mappedMonthlyData.find((item) => {
          const itemDate = moment(item.date).startOf('month');
          return itemDate.isSame(lastMonth, 'month') && item.status !== 'Rejected';
        });
        setMonthlyReportSubmitted(!!monthlyReport);
        setMonthlySubmissionDate(monthlyReport ? monthlyReport.date : null);
      } else {
        console.error('Monthly data is undefined or empty.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, [currentContactStore]);

  // Переходимо на потрібну вкладку якщо є ?tab= в URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) setActiveKey(tab);
  }, [location.search]);

  const onChange = (key) => {
    setActiveKey(key);
  };

  const handleAddClick = () => {
    setShowForm(true); // Show the form or ReportSubmitted component
  };

  const handleCloseClick = () => {
    setShowForm(false); // Close the form or ReportSubmitted component and show the table
  };

  const handleFormSubmit = () => {
    setShowForm(false); // Hide the form and show the table
    fetchData(); // Refresh the table data
  };

  const handleFilterTextChange = (e) => {
    setFilterText(e.target.value.toLowerCase());
  };

  const handleMenuClick = ({ key, domEvent }) => {
    // Prevent the dropdown from closing when clicking on menu items
    if (domEvent) {
      domEvent.stopPropagation();
    }

    if (key.startsWith('month-')) {
      setFilterMonth(parseInt(key.split('-')[1]));
    } else if (key.startsWith('year-')) {
      setFilterYear(parseInt(key.split('-')[1]));
    } else if (key === 'clear') {
      setFilterMonth(null);
      setFilterYear(null);
      setFilterVisible(false); // Close when explicitly clearing
    }
  };

  const handleVisibleChange = (flag) => {
    setFilterVisible(flag);
  };

  const filterData = (data) => {
    console.log('Filtering data with:', { filterMonth, filterYear, filterText });
    const filtered = data.filter((item) => {
      const itemDate = moment(item.date);
      const matchesMonth = filterMonth ? itemDate.month() + 1 === filterMonth : true;
      const matchesYear = filterYear ? itemDate.year() === filterYear : true;
      const matchesText = Object.values(item).join(' ').toLowerCase().includes(filterText);
      return matchesMonth && matchesYear && matchesText;
    });
    console.log('Filtered data:', filtered);
    return filtered;
  };

  const filteredDailyData = filterData(dailyData);
  console.log('Filtered daily data passed to table:', filteredDailyData);

  const filteredMonthlyData = filterData(monthlyData);
  console.log('Filtered monthly data passed to table:', filteredMonthlyData);

  const getUniqueYears = (data) => {
    const years = data.map((item) => moment(item.date).year());
    return [...new Set(years)].sort((a, b) => b - a); // Sort years in descending order
  };

  const dailyYears = getUniqueYears(dailyData);
  const monthlyYears = getUniqueYears(monthlyData);

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.SubMenu key="month" title="Місяць" popupClassName="menu-align-left">
        {moment.months().map((month, index) => (
          <Menu.Item
            key={`month-${index + 1}`}
            style={{
              backgroundColor: filterMonth === index + 1 ? '#E6F7FF' : 'transparent',
              color: filterMonth === index + 1 ? '#1890FF' : 'inherit',
            }}
          >
            {month}
          </Menu.Item>
        ))}
      </Menu.SubMenu>
      <Menu.SubMenu key="year" title="Рік" popupClassName="menu-align-left">
        {[...new Set([...dailyYears, ...monthlyYears])].map((year) => (
          <Menu.Item
            key={`year-${year}`}
            style={{
              backgroundColor: filterYear === year ? '#E6F7FF' : 'transparent',
              color: filterYear === year ? '#1890FF' : 'inherit',
            }}
          >
            {year}
          </Menu.Item>
        ))}
      </Menu.SubMenu>
      <Menu.Item key="clear">Очистити фільтри</Menu.Item>
    </Menu>
  );

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="commodity boxes">
      <div className="box commodity__header">
        <div className="box__title-inner" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="text-purple-dark-bold-f24-l34">Товарообіг</div>

          <div className="commodity__actions">
            {!showForm && (
              <div className="commodity__filters-container">
                <div
                  className={`collapsible-search ${searchExpanded || filterText ? 'collapsible-search--expanded' : ''}`}
                >
                  <Input
                    ref={searchInputRef}
                    placeholder={searchExpanded || filterText ? 'Пошук по всім полям...' : ''}
                    className="search-input"
                    value={filterText}
                    prefix={
                      <SearchOutlined
                        className="collapsible-search__icon"
                        onClick={() => {
                          setSearchExpanded(true);
                          setTimeout(() => searchInputRef.current?.focus(), 50);
                        }}
                      />
                    }
                    suffix={
                      (searchExpanded || filterText) && (
                        <CloseOutlined
                          className="collapsible-search__close"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterText('');
                            setSearchExpanded(false);
                          }}
                        />
                      )
                    }
                    onChange={handleFilterTextChange}
                    onFocus={() => setSearchExpanded(true)}
                    onBlur={() => {
                      if (!filterText) {
                        setSearchExpanded(false);
                      }
                    }}
                  />
                </div>

                <div className="commodity__filter-tools">
                  {!showForm && (filterMonth || filterYear) && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center',
                        background: '#F0F0F0',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {filterMonth && (
                        <Tag
                          closable
                          onClose={() => setFilterMonth(null)}
                          style={{
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            color: '#9A18FF',
                            border: 'none',
                            padding: '0 10px',
                            fontWeight: 500,
                            margin: 0,
                            height: '24px',
                            lineHeight: '24px',
                          }}
                        >
                          {moment.months()[filterMonth - 1]}
                        </Tag>
                      )}
                      {filterYear && (
                        <Tag
                          closable
                          onClose={() => setFilterYear(null)}
                          style={{
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            color: '#9A18FF',
                            border: 'none',
                            padding: '0 10px',
                            fontWeight: 500,
                            margin: 0,
                            height: '24px',
                            lineHeight: '24px',
                          }}
                        >
                          {filterYear}
                        </Tag>
                      )}
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          setFilterMonth(null);
                          setFilterYear(null);
                        }}
                        style={{
                          color: '#8c8c8c',
                          padding: 0,
                          height: '24px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        Очистити
                      </Button>
                    </div>
                  )}

                  <Dropdown
                    overlay={menu}
                    trigger={['click']}
                    visible={filterVisible}
                    onVisibleChange={handleVisibleChange}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '50%',
                        transition: 'background 0.3s ease',
                      }}
                      className="filter-icon-wrapper"
                    >
                      <img
                        src={IconGlyph}
                        alt="Фільтри"
                        style={{
                          width: '24px',
                          height: '24px',
                          transition: 'transform 0.2s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      />
                    </div>
                  </Dropdown>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="commodity__sub-header-row">
          <div className="commodity__left-actions">
            {activeKey === 'tradeMonthly' && (
              <Tooltip title={showForm ? 'Закрити' : 'Додати новий запис'}>
                <Button
                  type="primary"
                  shape="circle"
                  className="commodity__add-btn"
                  onClick={showForm ? handleCloseClick : handleAddClick}
                  style={{
                    backgroundColor: showForm ? '#9A18FF' : '#40E0D0',
                    borderColor: showForm ? '#9A18FF' : '#40E0D0',
                    color: '#fff',
                    fontSize: '20px',
                    width: 40,
                    height: 40,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {showForm ? '×' : '+'}
                </Button>
              </Tooltip>
            )}
          </div>

          <Tabs className="tabs commodity__tabs" activeKey={activeKey} onChange={onChange}>
            <TabPane key="tradeDaily" tab="Загальний товарообіг" />
            <TabPane key="tradeMonthly" tab="Товарообіг місячний" />
            <TabPane key="tradeReconciliation" tab="Цифровий ТО" />
          </Tabs>
        </div>
      </div>

      {activeKey === 'tradeDaily' ? (
        /* Commented out original Daily Turnover submission/table logic
        showForm ? (
          <div className="box commodity__content">
            {dailyReportSubmitted ? (
              <ReportSubmitted isDaily={true} submissionDate={dailySubmissionDate} />
            ) : (
              <CommodityDaily
                activeKey={activeKey}
                disabled={false}
                currentContactStore={currentContactStore}
                onFormSubmit={handleFormSubmit}
              />
            )}
          </div>
        ) : (
          <div className="box table_content">
            <CommodityTable
              data={filteredDailyData}
              isDaily={true}
              currentContactStore={currentContactStore}
            />
          </div>
        )
        */
        <div className="box commodity__content commodity__content--full">
          <CommodityComparisonTable
            dailyData={dailyData}
            monthlyData={monthlyData}
            currentContactStore={currentContactStore}
            filterMonth={filterMonth}
            filterYear={filterYear}
            filterText={filterText}
          />
        </div>
      ) : activeKey === 'tradeMonthly' ? (
        showForm ? (
          <div className="box commodity__content commodity__content--full">
            {monthlyReportSubmitted ? (
              <ReportSubmitted isDaily={false} submissionDate={monthlySubmissionDate} />
            ) : (
              <CommodityMonthly
                activeKey={activeKey}
                currentContactStore={currentContactStore}
                onFormSubmit={handleFormSubmit}
              />
            )}
          </div>
        ) : (
          <div className="box table_content">
            <CommodityTable
              data={filteredMonthlyData}
              isDaily={false}
              currentContactStore={currentContactStore}
            />
          </div>
        )
      ) : activeKey === 'tradeReconciliation' ? (
        <div className="box commodity__content commodity__content--full">
          <CommodityVerification
            currentContactStore={currentContactStore}
            onTabChange={setActiveKey}
            dailyData={dailyData}
            monthlyData={monthlyData}
            mode="reconciliation"
            filterMonth={filterMonth}
            filterYear={filterYear}
            filterText={filterText}
          />
        </div>
      ) : null}
    </div>
  );
};

export default Commodity;
